import type * as SelectPrimitive from "@radix-ui/react-select";
import type {
  ComponentPropsWithoutRef,
  ComponentRef,
  HTMLAttributes,
  ReactNode,
} from "react";

import type { TIconKey } from "@/ui/components/primitives/Icon";
import type { DefinedDefaults } from "@/ui/lib";

/*-------------------------------------
|  Constants
|-------------------------------------*/

export const SELECT_TRIGGER_ICON: TIconKey = "chevronDown";
export const SELECT_ITEM_INDICATOR_ICON: TIconKey = "tick";
export const SELECT_SCROLL_UP_ICON: TIconKey = "chevronUp";
export const SELECT_SCROLL_DOWN_ICON: TIconKey = "chevronDown";

export const SELECT_CONTENT_POSITION_CLASS_MAP = {
  itemAligned: "",
  popper: "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
} as const;

/*-------------------------------------
|  Types
|-------------------------------------*/

export type TSelectOption = {
  disabled?: boolean;
  icon?: TIconKey;
  label: string;
  value: string;
};

export type TSelectRootProps = ComponentPropsWithoutRef<typeof SelectPrimitive.Root>;
export type TSelectGroupProps = ComponentPropsWithoutRef<typeof SelectPrimitive.Group>;
export type TSelectValueProps = ComponentPropsWithoutRef<typeof SelectPrimitive.Value>;
export type TSelectTriggerElement = ComponentRef<typeof SelectPrimitive.Trigger>;
export type TSelectTriggerProps = ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & {
  error?: string;
};
export type TSelectContentElement = ComponentRef<typeof SelectPrimitive.Content>;
export type TSelectContentPosition = keyof typeof SELECT_CONTENT_POSITION_CLASS_MAP;
export type TSelectContentProps = ComponentPropsWithoutRef<typeof SelectPrimitive.Content> & {
  position?: TSelectContentPosition;
};
export type TSelectLabelElement = ComponentRef<typeof SelectPrimitive.Label>;
export type TSelectLabelProps = ComponentPropsWithoutRef<typeof SelectPrimitive.Label>;
export type TSelectItemElement = ComponentRef<typeof SelectPrimitive.Item>;
export type TSelectItemProps = ComponentPropsWithoutRef<typeof SelectPrimitive.Item> & {
  icon?: TIconKey;
};
export type TSelectSeparatorElement = ComponentRef<typeof SelectPrimitive.Separator>;
export type TSelectSeparatorProps = ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>;
export type TSelectScrollUpButtonElement = ComponentRef<typeof SelectPrimitive.ScrollUpButton>;
export type TSelectScrollUpButtonProps = ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>;
export type TSelectScrollDownButtonElement = ComponentRef<typeof SelectPrimitive.ScrollDownButton>;
export type TSelectScrollDownButtonProps = ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>;
export type TSelectViewportElement = ComponentRef<typeof SelectPrimitive.Viewport>;
export type TSelectViewportProps = ComponentPropsWithoutRef<typeof SelectPrimitive.Viewport> & {
  position?: TSelectContentPosition;
};

export type TSelectProps = TSelectRootProps & {
  className?: string;
  contentClassName?: string;
  error?: string;
  label: string;
  options: TSelectOption[];
  placeholder?: ReactNode;
  triggerClassName?: string;
  hideLabel?: boolean;
} & Pick<HTMLAttributes<HTMLElement>, "aria-label" | "aria-labelledby" | "id">;

type TSelectDefaultProps = DefinedDefaults<
  TSelectProps,
  "placeholder"
>;

type TSelectContentDefaultProps = DefinedDefaults<
  TSelectContentProps,
  "position"
>;

/*-------------------------------------
|  Defaults
|-------------------------------------*/

export const SELECT_DEFAULTS = {
  placeholder: "Select an option",
} satisfies TSelectDefaultProps;

export const SELECT_CONTENT_DEFAULTS = {
  position: "popper",
} satisfies TSelectContentDefaultProps;
